/**
 * Pure announcement-generator (tekst; de audio-adapter spreekt het uit).
 * Geen afhankelijkheden: deterministische tekst uit state + laatste effecten.
 * Voorbeeld: "Twee–nul. Punt Team A. Noor serveert."
 */

import type { Effect, MatchState, PlayerIndex, TeamId } from "./types";

const UNITS_NL = [
  "nul", "een", "twee", "drie", "vier", "vijf", "zes", "zeven", "acht", "negen",
  "tien", "elf", "twaalf", "dertien", "veertien", "vijftien", "zestien",
  "zeventien", "achttien", "negentien", "twintig",
];

export function numberToDutch(n: number): string {
  if (n < 0) return String(n);
  if (n <= 20) return UNITS_NL[n];
  if (n < 100) {
    const tens = Math.floor(n / 10);
    const units = n % 10;
    const tensWord = tens === 2 ? "twintig" : tens === 3 ? "dertig" : tens === 4 ? "veertig" : tens === 5 ? "vijftig" : tens === 6 ? "zestig" : tens === 7 ? "zeventig" : tens === 8 ? "tachtig" : "negentig";
    return units === 0 ? tensWord : `${UNITS_NL[units]}en${tensWord}`;
  }
  return String(n);
}

export function scoreText(state: MatchState): string {
  const { points } = state.currentSet;
  return `${numberToDutch(points.A)}–${numberToDutch(points.B)}`;
}

export function playerNameAtOrderPosition(state: MatchState, team: TeamId, orderPosition: PlayerIndex): string {
  const firstInOrder = state.currentSet.firstInOrder[team];
  const playerIdx = orderPosition === 0 ? firstInOrder : ((1 - firstInOrder) as PlayerIndex);
  return state.config.teams[team].players[playerIdx];
}

export function teamName(state: MatchState, team: TeamId): string {
  return state.config.teams[team].name;
}

export interface Announcement {
  text: string;
  kind: "point" | "correction" | "set" | "match" | "side_switch" | "warning";
}

/**
 * Bepaal de aankondigingen voor een event. De audio-adapter debounced en
 * voorkomt overlap; de UI toont de tekst.
 */
export function buildAnnouncements(state: MatchState, effects: readonly Effect[]): Announcement[] {
  const out: Announcement[] = [];
  for (const e of effects) {
    switch (e.kind) {
      case "point_scored": {
        const server = state.nextServe;
        const serverPart = server ? ` ${playerNameAtOrderPosition(state, server.team, server.playerIndex)} serveert.` : "";
        out.push({
          kind: "point",
          text: `${scoreText(state)}. Punt ${teamName(state, e.team)}.${serverPart}`,
        });
        break;
      }
      case "point_confirmed":
        // Bevestigende opslag: NIET herhalen (opdracht: "Herhaal niet bij de
        // bevestigende opslag."). Alleen wanneer de gebruiker met de hand
        // bevestigde (medium voorstel -> bevestigknop) is een aankondiging
        // zinvol; die kwam al als point_scored.
        break;
      case "point_corrected":
        out.push({ kind: "correction", text: `Correctie, punt ${teamName(state, e.to)}.` });
        break;
      case "side_switch_pending":
        out.push({ kind: "side_switch", text: "Kantwissel." });
        break;
      case "side_switch_confirmed":
        out.push({ kind: "side_switch", text: "Kantwissel bevestigd." });
        break;
      case "set_won":
        out.push({ kind: "set", text: `Set gewonnen door ${teamName(state, e.team)}: ${numberToDutch(e.points.A)}–${numberToDutch(e.points.B)}.` });
        break;
      case "match_won":
        out.push({ kind: "match", text: `Wedstrijd gewonnen door ${teamName(state, e.team)}!` });
        break;
      case "warning":
        out.push({ kind: "warning", text: e.message });
        break;
      case "serve_deferred":
      case "ask_user":
        // geen aankondiging; UI toont "controle nodig"
        break;
      default:
        break;
    }
  }
  return out;
}
